import { Skeleton } from "@/components/kit";
import { cn } from "@/lib/utils";
import type {
  CustomJobBoardColumnKey,
  CustomJobListItemDTO,
} from "@/modules/custom-jobs/domain/types";
import { CustomJobKanbanCard } from "./CustomJobKanbanCard";

type CustomJobKanbanColumnProps = {
  title: string;
  count: number;
  tone: "muted" | "secondary" | "primary" | "leaf";
  jobs: CustomJobListItemDTO[];
  isLoading?: boolean;
  canManage: boolean;
  onView: (id: string) => void;
  onConfirm?: (id: string) => void;
  onStartWork?: (id: string) => void;
  onMarkReady?: (id: string) => void;
  onComplete?: (id: string) => void;
  pendingAction?: {
    id: string;
    action: CustomJobBoardColumnKey | "complete";
  } | null;
};

const toneStyles = {
  muted: "border-border/60 bg-muted/30",
  secondary: "border-secondary/30 bg-secondary/10",
  primary: "border-primary/25 bg-primary/5",
  leaf: "border-leaf/30 bg-leaf/10",
} as const;

const toneBadge = {
  muted: "bg-muted text-muted-foreground",
  secondary: "bg-secondary text-secondary-foreground",
  primary: "bg-primary text-primary-foreground",
  leaf: "bg-leaf text-leaf-foreground",
} as const;

export function CustomJobKanbanColumn({
  title,
  count,
  tone,
  jobs,
  isLoading,
  canManage,
  onView,
  onConfirm,
  onStartWork,
  onMarkReady,
  onComplete,
  pendingAction,
}: CustomJobKanbanColumnProps) {
  return (
    <section
      className={cn(
        "flex min-h-[280px] flex-col gap-3 rounded-2xl border p-3 sm:p-4",
        toneStyles[tone],
      )}
    >
      <header className="flex items-center justify-between gap-2">
        <h3 className="font-display text-foreground text-lg">{title}</h3>
        <span
          className={cn(
            "rounded-full px-2.5 py-0.5 font-medium text-sm tabular-nums",
            toneBadge[tone],
          )}
        >
          {count}
        </span>
      </header>

      <div className="flex flex-1 flex-col gap-3">
        {isLoading ? (
          ["a", "b", "c"].map((id) => (
            <Skeleton key={id} className="h-36 rounded-xl" />
          ))
        ) : jobs.length === 0 ? (
          <p className="py-8 text-center text-muted-foreground text-sm">
            ບໍ່ມີງານ
          </p>
        ) : (
          jobs.map((job) => (
            <CustomJobKanbanCard
              key={job.id}
              job={job}
              canManage={canManage}
              onView={onView}
              onConfirm={onConfirm}
              onStartWork={onStartWork}
              onMarkReady={onMarkReady}
              onComplete={onComplete}
              isConfirmPending={
                pendingAction?.id === job.id &&
                pendingAction.action === "pending_confirm"
              }
              isStartPending={
                pendingAction?.id === job.id &&
                pendingAction.action === "queued"
              }
              isMarkReadyPending={
                pendingAction?.id === job.id &&
                pendingAction.action === "in_progress"
              }
              isCompletePending={
                pendingAction?.id === job.id &&
                pendingAction.action === "complete"
              }
            />
          ))
        )}
      </div>
    </section>
  );
}

export const CUSTOM_JOB_BOARD_COLUMNS: Array<{
  key: CustomJobBoardColumnKey;
  title: string;
  tone: CustomJobKanbanColumnProps["tone"];
}> = [
    { key: "pending_confirm", title: "ລໍຖ້າຢືນຢັນ", tone: "muted" },
    { key: "queued", title: "ລໍຖ້າເລີ່ມ", tone: "secondary" },
    { key: "in_progress", title: "ກຳລັງເຮັດ", tone: "primary" },
    { key: "ready", title: "ພ້ອມຮັບ", tone: "leaf" },
  ];
